/**
 * BMW D-CAN (K+DCAN cable) raw protocol engine — BMW-FAST telegram framing.
 *
 * Unlike ELM327 adapters, a plain K+DCAN cable (the kind bundled with INPA/ISTA)
 * does NOT speak AT commands. On D-CAN cars it expects BMW-FAST telegrams sent as
 * raw bytes over the FTDI serial link at 115200 8N1, and it echoes back every byte
 * you send (local echo) before the ECU response arrives.
 *
 * BMW-FAST telegram layout:
 *   [fmt] [target] [source] ([len]) [data...] [checksum]
 *   - fmt      : 0b10LLLLLL  (0x80 | length). If length (0..63) == 0, a separate
 *                length byte follows the source address.
 *   - target   : ECU address (DME = 0x12)
 *   - source   : tester address (0xF1)
 *   - checksum : 8-bit sum of ALL preceding bytes, mod 256
 *
 * For live datalogging we wrap standard OBD-II Mode 01 requests ([0x01, PID]) in
 * BMW-FAST framing, which works on any 2008+ D-CAN BMW without needing SGBD files.
 */

export const DME_ADDR = 0x12;   // Engine ECU (DME) physical address
export const TESTER_ADDR = 0xF1; // Diagnostic tester address

export function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ');
}

/** 8-bit additive checksum (mod 256) over the given byte array. */
export function checksum8(bytes) {
  return bytes.reduce((acc, b) => (acc + b) & 0xff, 0);
}

/**
 * Build a BMW-FAST telegram. Returns Uint8Array ready to write to the serial port.
 * @param {number[]} data - payload bytes (e.g. [0x01, 0x0C] for OBD-II Mode 01 RPM)
 */
export function buildFastFrame(data, target = DME_ADDR, source = TESTER_ADDR) {
  const len = data.length;
  const header = [];
  if (len > 0 && len <= 0x3f) {
    header.push(0x80 | len, target, source);
  } else {
    // length overflow → fmt with zero length + explicit length byte after source
    header.push(0x80, target, source, len & 0xff);
  }
  const body = [...header, ...data];
  const frame = [...body, checksum8(body)];
  return new Uint8Array(frame);
}

/** Convert an OBD pid string like "010C" into raw request bytes [0x01, 0x0C]. */
export function pidStringToBytes(pid) {
  const clean = pid.replace(/[^0-9a-fA-F]/g, '');
  const out = [];
  for (let i = 0; i + 1 < clean.length; i += 2) {
    out.push(parseInt(clean.substr(i, 2), 16));
  }
  return out;
}

/**
 * Extract complete, checksum-valid BMW-FAST frames from an accumulating byte
 * buffer. Naturally resynchronises past local echo / garbage by scanning for a
 * valid format byte and verifying the trailing checksum.
 *
 * @param {number[]} bytes - accumulated RX bytes
 * @returns {{ frames: number[][], rest: number[] }}
 */
export function extractFrames(bytes) {
  const frames = [];
  let i = 0;
  while (i < bytes.length) {
    const fmt = bytes[i];
    // A valid BMW-FAST format byte has the top two bits = 0b10.
    if ((fmt & 0xc0) !== 0x80) {
      i += 1;
      continue;
    }
    let len = fmt & 0x3f;
    let dataStart = 3;
    if (len === 0) {
      if (i + 3 >= bytes.length) break; // need the explicit length byte
      len = bytes[i + 3];
      dataStart = 4;
    }
    const totalLen = dataStart + len + 1; // + checksum byte
    if (i + totalLen > bytes.length) break; // incomplete → wait for more bytes
    const frame = bytes.slice(i, i + totalLen);
    const sum = checksum8(frame.slice(0, totalLen - 1));
    if (sum === frame[totalLen - 1]) {
      frames.push(frame);
      i += totalLen;
    } else {
      i += 1; // bad checksum → resync one byte forward
    }
  }
  return { frames, rest: bytes.slice(i) };
}

/** Parse a validated frame into { target, source, data }. */
export function parseFrame(frame) {
  const fmt = frame[0];
  let len = fmt & 0x3f;
  let dataStart = 3;
  if (len === 0) {
    len = frame[3];
    dataStart = 4;
  }
  return {
    target: frame[1],
    source: frame[2],
    data: frame.slice(dataStart, dataStart + len),
  };
}

/**
 * From a parsed frame addressed to the tester, pull the OBD-II Mode 01 response.
 * Response data looks like [0x41, PID, ...payload].
 * @returns {{ pidCode: string, payloadHex: string } | null}
 */
export function decodeMode01Response(frame) {
  const { target, data } = parseFrame(frame);
  // Only frames addressed back to the tester are real responses (drops echoes).
  if (target !== TESTER_ADDR) return null;
  if (data.length < 2 || data[0] !== 0x41) return null;
  const pidCode = data[1].toString(16).padStart(2, '0').toUpperCase();
  const payloadHex = data
    .slice(2)
    .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
    .join('');
  return { pidCode, payloadHex };
}
